import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useSessionContext } from "../context/SessionContext";
import { C } from "../theme";
import Card from "../components/Card";
import CollapsibleSection from "../components/CollapsibleSection";
import Badge from "../components/Badge";
import useGeolocation from "../hooks/useGeolocation";
import useTideStage from "../hooks/useTideStage";
import usePatternTier, { TIER_THRESHOLDS } from "../hooks/usePatternTier";
import { requestPatternInsight } from "../services/aiClient";

// ── factsKey → human label ─────────────────────────────────────────────
// Mirrors the bucket keys emitted by pattern_facts.py. Update both files
// together when adding new fact axes.
const TIDE_LABEL = {
  rising: "Rising Tide",
  falling: "Falling Tide",
  high_slack: "High Slack",
  low_slack: "Low Slack",
};
const BARO_LABEL = {
  rising: "Rising Pressure",
  falling: "Falling Pressure",
  steady: "Steady Pressure",
};
const MOON_LABEL = {
  new: "New Moon",
  waxing: "Waxing Moon",
  full: "Full Moon",
  waning: "Waning Moon",
};
const TEMP_LABEL = {
  cold: "Cold Water (<62°F)",
  cool: "Cool Water (62–68°F)",
  warm: "Warm Water (68–76°F)",
  hot: "Hot Water (>76°F)",
};
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function titleCase(raw) {
  return String(raw || "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function monthLabel(yyyyMm) {
  const [, mm] = String(yyyyMm).split("-");
  const idx = Number(mm) - 1;
  return MONTH_NAMES[idx] || yyyyMm;
}

function labelForFactsKey(factsKey) {
  if (!factsKey) return "Pattern";
  const [root, ...tailParts] = factsKey.split(".");
  const tail = tailParts.join(".");

  switch (root) {
    case "byTideStage":
      return TIDE_LABEL[tail] || titleCase(tail);
    case "byBaroTrend":
      return BARO_LABEL[tail] || titleCase(tail);
    case "byMoonPhase":
      return MOON_LABEL[tail] || titleCase(tail);
    case "byWaterTemp":
      return TEMP_LABEL[tail] || titleCase(tail);
    case "byWaterClarity":
      return `${titleCase(tail)} Water`;
    case "byHourLocal": {
      const h = Number(tail);
      if (Number.isFinite(h)) {
        const suffix = h >= 12 ? "PM" : "AM";
        const display = h % 12 === 0 ? 12 : h % 12;
        return `${display} ${suffix} Window`;
      }
      return titleCase(tail);
    }
    case "byMonth":
      return monthLabel(tail);
    case "byLure":
      return titleCase(tail);
    case "bySpecies":
      return titleCase(tail);
    case "lureXBaroTrend": {
      const [lure, baro] = tail.split("__");
      return `${titleCase(lure)} + ${BARO_LABEL[baro] || titleCase(baro)}`;
    }
    case "monthXTideStage": {
      const [month, tide] = tail.split("__");
      return `${monthLabel(month)} ${TIDE_LABEL[tide] || titleCase(tide)}`;
    }
    case "speciesXWaterTemp": {
      const [species, band] = tail.split("__");
      return `${titleCase(species)} in ${TEMP_LABEL[band] || titleCase(band)}`;
    }
    case "byCompound": {
      const [tide, baro, moon] = tail.split("__");
      return `${TIDE_LABEL[tide] || titleCase(tide)} + ${BARO_LABEL[baro] || titleCase(baro)} + ${MOON_LABEL[moon] || titleCase(moon)}`;
    }
    case "hotspots":
      return `Hotspot ${tail.replace(/^hs_/, "#")}`;
    case "topSessions":
      return "Personal Best Session";
    default:
      return titleCase(tail || root);
  }
}

// Color a card by the dimension its factsKey lives in — so similar
// patterns visually group on the screen.
function colorForFactsKey(factsKey) {
  const root = String(factsKey || "").split(".")[0];
  switch (root) {
    case "byTideStage":
    case "monthXTideStage":
      return C.teal;
    case "byBaroTrend":
    case "lureXBaroTrend":
      return C.sand;
    case "byMoonPhase":
      return C.warning;
    case "byLure":
      return C.gold;
    case "bySpecies":
    case "speciesXWaterTemp":
      return C.accent;
    case "byWaterTemp":
    case "byWaterClarity":
      return C.seafoam;
    case "byHourLocal":
      return C.accent;
    case "hotspots":
      return C.seafoam;
    case "byCompound":
      return C.gold;
    default:
      return C.seafoam;
  }
}

function strengthForConfidence(conf) {
  const c = String(conf || "").toLowerCase();
  if (c === "high") return 90;
  if (c === "medium") return 70;
  return 50;
}

export default function PatternsScreen() {
  const { userId } = useSessionContext();
  // Use `position` from the hook as our `location` ({ lat, lng, ... }).
  const { position: location } = useGeolocation();
  const [monthlyCatches, setMonthlyCatches] = useState(new Array(12).fill(0));
  const [sessionCount, setSessionCount] = useState(0);
  const [showMonthChart, setShowMonthChart] = useState(true);

  // Station context — mirrors Dashboard. Read the persisted selection
  // and resolve a name via useTideStage so analyses are scoped to the
  // same water the user sees on the dashboard.
  const persistedStationId = (() => {
    try {
      return localStorage.getItem("the99.stationId");
    } catch {
      return null;
    }
  })();
  const { stationName, stationId: autoStationId } = useTideStage({
    lat: location?.lat,
    lng: location?.lng,
    enabled: !!location,
    stationId: persistedStationId,
  });
  const effectiveStationId = persistedStationId || autoStationId || null;

  const patternTier = usePatternTier(userId);

  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  // ── Monthly catch chart (local Firestore aggregation, unchanged) ────
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function loadCatches() {
      const sessionsSnap = await getDocs(collection(db, "users", userId, "sessions"));
      if (cancelled) return;
      const counts = new Array(12).fill(0);

      await Promise.all(
        sessionsSnap.docs.map(async (sessionDoc) => {
          const catchesSnap = await getDocs(
            collection(db, "users", userId, "sessions", sessionDoc.id, "catches")
          );
          catchesSnap.docs.forEach((catchDoc) => {
            const data = catchDoc.data() || {};
            // Skip missed fish — the monthly chart counts landed catches only.
            if (data.outcome === "missed") return;
            const caughtAt = data.caughtAt;
            let month = null;
            if (typeof caughtAt?.toDate === "function") {
              month = caughtAt.toDate().getMonth();
            } else if (typeof caughtAt?.seconds === "number") {
              month = new Date(caughtAt.seconds * 1000).getMonth();
            }
            if (month !== null && month >= 0 && month <= 11) counts[month]++;
          });
        })
      );

      if (!cancelled) {
        setMonthlyCatches(counts);
        setSessionCount(sessionsSnap.size);
      }
    }

    loadCatches().catch(console.error);
    return () => { cancelled = true; };
  }, [userId]);

  // ── Pattern Insight fetch (tier-gated, station-scoped, 24h cached) ──
  useEffect(() => {
    if (!userId || !effectiveStationId) {
      setInsight(null);
      setInsightError(null);
      setInsightLoading(false);
      return;
    }
    if (patternTier.loading) return;
    if (patternTier.effectiveTier < 3) {
      // Tier 0/1/2 — no API call. Keep cost at $0.
      setInsight(null);
      setInsightError(null);
      setInsightLoading(false);
      return;
    }
    let cancelled = false;
    setInsightLoading(true);
    setInsightError(null);
    requestPatternInsight({
      userId,
      targetStationId: effectiveStationId,
      targetStationName: stationName || null,
    })
      .then((data) => {
        if (cancelled) return;
        setInsight(data || null);
        setFromCache(!!data?._fromCache);
      })
      .catch((err) => {
        if (cancelled) return;
        setInsight(null);
        setInsightError(err?.message || String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setInsightLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, effectiveStationId, stationName, patternTier.loading, patternTier.effectiveTier]);

  const result = insight?.result || null;
  const supportingPatterns = useMemo(() => {
    const items = Array.isArray(result?.supportingPatterns) ? result.supportingPatterns : [];
    return items.slice(0, 6);
  }, [result]);
  const recommendations = Array.isArray(result?.recommendedAdjustments)
    ? result.recommendedAdjustments
    : [];
  const caveats = Array.isArray(result?.caveats) ? result.caveats : [];

  const tierBlocked = patternTier.effectiveTier < 3;
  // AI turns on at this many catch-sessions — keep the scale identical to the
  // Preparation Dashboard (AI_UNLOCK_SESSIONS = TIER_THRESHOLDS[3] = 10) so the
  // seeding progress shown here matches what the angler sees there.
  const AI_UNLOCK_SESSIONS = TIER_THRESHOLDS[3]; // 10
  const tierBanner =
    tierBlocked &&
    (() => {
      const next = AI_UNLOCK_SESSIONS;
      const done = Math.min(patternTier.catchSessions, next);
      const remaining = Math.max(0, next - patternTier.catchSessions);
      const pct = next > 0 ? Math.min(100, Math.round((done / next) * 100)) : 0;
      const tierLabel =
        patternTier.effectiveTier === 0
          ? `TIER 0 · LOCKED — ${done}/${next}`
          : `TIER 1 · SEEDING — ${done}/${next}`;
      return (
        <div style={{ margin: "0 14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2 }}>AI PATTERN INSIGHT</div>
            <Badge color={C.muted} style={{ fontSize: 9 }}>
              {tierLabel}
            </Badge>
          </div>
          <Card style={{ padding: "12px 14px", borderColor: C.gold + "44", background: `${C.gold}08` }}>
            {patternTier.effectiveTier === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                  Tier 0 · Locked — your patterns will appear here.
                </div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                  Log a few trips and we'll start spotting what's working — tide, moon, water temp, and bait combos that put fish on your deck.
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                  Pattern Insight unlocks after <strong style={{ color: C.sand }}>{next}</strong> trips with at least one catch.
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                  Tier 1 · Seeding — building your fingerprint
                </div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                  We need a little more data before AI Pattern Insight can spot reliable trends in your fishing. AI turns on at <strong style={{ color: C.sand }}>{next}</strong> trips with a catch.
                </div>
                <div>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: `${C.muted}33`,
                      overflow: "hidden",
                    }}
                    aria-hidden="true"
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: C.sand,
                        transition: "width 240ms ease-out",
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 6, display: "flex", justifyContent: "space-between" }}>
                    <span>
                      <strong style={{ color: C.sand }}>{done}</strong> / {next} trips with catches
                    </span>
                    <span>{remaining === 0 ? "Unlocking…" : `${remaining} more to unlock`}</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      );
    })();

  return (
    <div className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", paddingBottom: 70 }}>
      <div style={{ padding: "18px 18px 8px" }}>
        <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
        <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>Your Patterns</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          {sessionCount > 0 ? `Surfaced from ${sessionCount} session${sessionCount !== 1 ? "s" : ""}` : "No sessions yet"}
          {stationName ? ` · ${stationName}` : ""}
          {fromCache ? " · cached" : ""}
        </div>
      </div>

      {/* Catch rate by month — local Firestore aggregation, always shown */}
      <div style={{ margin: "8px 14px 12px" }}>
        <CollapsibleSection
          open={showMonthChart}
          onToggle={() => setShowMonthChart(v => !v)}
          headerTitle={showMonthChart ? "Hide chart" : "Show chart"}
          bodyStyle={{ padding: "0 12px 14px" }}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>CATCH RATE BY MONTH</span>}
        >
          {(() => {
            const maxCount = Math.max(...monthlyCatches, 1);
            const peakMonth = monthlyCatches.indexOf(Math.max(...monthlyCatches));
            const totalCatches = monthlyCatches.reduce((a, b) => a + b, 0);
            const topMonths = [...monthlyCatches]
              .map((v, i) => ({ v, i }))
              .filter(({ v }) => v > 0)
              .sort((a, b) => b.v - a.v)
              .slice(0, 2)
              .map(({ i }) => "JanFebMarAprMayJunJulAugSepOctNovDec".match(/.{3}/g)[i]);
            return (
              <>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 72 }}>
                  {"JFMAMJJASOND".split("").map((letter, i) => {
                    const v = monthlyCatches[i];
                    const barHeight = v > 0 ? Math.max(Math.round((v / maxCount) * 50), 8) : 2;
                    const isPeak = i === peakMonth && v > 0;
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <div style={{ fontSize: 7, fontWeight: 700, color: isPeak ? C.accent : C.teal, minHeight: 9, lineHeight: "9px", textAlign: "center" }}>
                          {v > 0 ? v : ""}
                        </div>
                        <div
                          style={{
                            width: "100%",
                            height: barHeight,
                            background: isPeak ? C.accent : C.teal,
                            borderRadius: "2px 2px 0 0",
                            opacity: 0.85,
                          }}
                        />
                        <div style={{ fontSize: 7, color: C.muted }}>{letter}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: 9, color: C.accent, fontStyle: "italic", textAlign: "center" }}>
                  {totalCatches === 0
                    ? "No catches logged yet"
                    : topMonths.length === 1
                    ? `↑ ${topMonths[0]} is your highest-confidence month`
                    : `↑ ${topMonths.join(" & ")} are your highest-confidence months`}
                </div>
              </>
            );
          })()}
        </CollapsibleSection>
      </div>

      {tierBlocked && tierBanner}

      {!tierBlocked && !effectiveStationId && (
        <div style={{ margin: "8px 14px 12px" }}>
          <Card style={{ padding: "14px 14px" }}>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
              Select a tide station on the Dashboard to see your station-scoped patterns.
            </div>
          </Card>
        </div>
      )}

      {!tierBlocked && effectiveStationId && insightLoading && !insight && (
        <div style={{ margin: "8px 14px 12px" }}>
          <Card style={{ padding: "14px 14px" }}>
            <div style={{ fontSize: 11, color: C.seafoam, letterSpacing: 1 }}>
              Computing your patterns from {stationName || "this station"}…
            </div>
          </Card>
        </div>
      )}

      {!tierBlocked && effectiveStationId && insightError && !insight && (
        <div style={{ margin: "8px 14px 12px" }}>
          <Card style={{ padding: "14px 14px", borderColor: C.warning + "55" }}>
            <div style={{ fontSize: 10, color: C.warning, letterSpacing: 2, marginBottom: 6 }}>
              PATTERN INSIGHT UNAVAILABLE
            </div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
              {insightError}
            </div>
          </Card>
        </div>
      )}

      {!tierBlocked && result && supportingPatterns.length > 0 && supportingPatterns.map((p, i) => {
        const title = labelForFactsKey(p.factsKey);
        const color = colorForFactsKey(p.factsKey);
        const strength = strengthForConfidence(p.confidence);
        return (
          <div key={`${p.factsKey || "p"}-${i}`} style={{ margin: "0 14px 10px" }}>
            <Card style={{ padding: "12px 14px", borderLeft: `3px solid ${color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif", flex: 1, paddingRight: 8 }}>
                  {title}
                </div>
                <div style={{ textAlign: "center", minWidth: 40 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color, fontFamily: "Georgia,serif" }}>{strength}%</div>
                  <div style={{ fontSize: 7, color: C.muted, letterSpacing: 1 }}>CONF.</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{p.claim}</div>
              <div style={{ marginTop: 8, height: 3, background: "#1e3448", borderRadius: 2 }}>
                <div style={{ height: 3, width: `${strength}%`, background: color, borderRadius: 2 }} />
              </div>
            </Card>
          </div>
        );
      })}

      {!tierBlocked && result && supportingPatterns.length === 0 && !insightLoading && (
        <div style={{ margin: "8px 14px 12px" }}>
          <Card style={{ padding: "14px 14px" }}>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
              No high-confidence patterns surfaced yet for {stationName || "this station"}. Keep logging trips and the analysis will sharpen.
            </div>
          </Card>
        </div>
      )}

      {!tierBlocked && recommendations.length > 0 && (
        <div style={{ margin: "8px 14px 10px" }}>
          <Card style={{ padding: "12px 14px" }}>
            <div style={{ fontSize: 9, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>
              RECOMMENDED ADJUSTMENTS
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              {recommendations.map((r, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{r}</li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {!tierBlocked && result?.headline && result?.summary && (
        <div style={{ margin: "0 14px 12px" }}>
          <Card style={{ padding: "12px 14px", borderColor: C.gold + "44", background: `${C.gold}06` }}>
            <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, marginBottom: 6 }}>
              COMPOUND PATTERN · {String(result.confidence || "medium").toUpperCase()} CONFIDENCE
            </div>
            <div style={{ fontSize: 12, color: C.sand, fontFamily: "Georgia,serif", fontWeight: 700, marginBottom: 6 }}>
              {result.headline}
            </div>
            <div style={{ fontSize: 12, color: C.sand, fontFamily: "Georgia,serif", fontStyle: "italic", lineHeight: 1.6 }}>
              "{result.summary}"
            </div>
          </Card>
        </div>
      )}

      {!tierBlocked && caveats.length > 0 && (
        <div style={{ margin: "0 14px 16px", fontSize: 9, color: C.muted, lineHeight: 1.5, fontStyle: "italic" }}>
          {caveats.join(" · ")}
        </div>
      )}
    </div>
  );
}
